import * as React from 'react';
import { ApolloError } from 'apollo-client';

// @ts-ignore
import AddedDeviceVerifierModal from 'app/components/AddedDeviceVerifierModal';
import Avatar from 'components/Avatar';
import Button from 'components/ButtonDeprecated';
import ErrorReporter from 'app/components/ErrorReporter';
// @ts-ignore
import LinkDeviceModal from 'user/components/LinkDeviceModal';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import Text from 'components/Text';
import SETTINGS_ROUTES from 'settings/constants/SettingsRouteConstants';
import { UserSMSDeviceActionButtonAttributes } from './UserSMSDevice';
import {
  Devices_me_pendingSmsDevices,
  Devices_devices,
} from 'settings/queries/__generated__/Devices';

type Props = {
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
  enabledSMSDevices: Devices_devices[];
  disabledSMSDevices: Devices_devices[];
  pendingSMSDevices: Devices_me_pendingSmsDevices[];
  actionButtonAttributes?: UserSMSDeviceActionButtonAttributes;
};

export default function UserSMSDeviceView(props: Props) {
  const {
    loading,
    error,
    refetch,
    enabledSMSDevices,
    disabledSMSDevices,
    pendingSMSDevices,
    actionButtonAttributes,
  } = props;
  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorReporter error={error} />;
  }

  if (enabledSMSDevices.length > 0) {
    const firstDevice = enabledSMSDevices[0];
    const label = (
      <TextWithSubtext title={firstDevice.label || firstDevice.endpoint} subtext="Phone" />
    );
    return (
      <PhoneAvatarWithText
        data-test={`enabled-device-${firstDevice.id}`}
        actionButtonAttributes={actionButtonAttributes}
        label={label}
        deviceUuid={firstDevice.uuid}
      />
    );
  }

  if (disabledSMSDevices.length > 0) {
    const firstDevice = disabledSMSDevices[0];
    const labelSubtext = (
      <React.Fragment>
        This number has notifications turned off. In order to receive the test message, please
        enable notifications in your{' '}
        <Link to={SETTINGS_ROUTES.NOTIFICATIONS} target="_blank">
          Notification Settings
        </Link>
        .
      </React.Fragment>
    );

    return (
      <PhoneAvatarWithText
        data-test={`disabled-device-${firstDevice.id}`}
        actionButtonAttributes={actionButtonAttributes}
        actionDisabled
        label={
          <TextWithSubtext
            title={firstDevice.label || firstDevice.endpoint}
            subtext={labelSubtext}
          />
        }
      />
    );
  }

  if (pendingSMSDevices.length > 0) {
    const firstDevice = pendingSMSDevices[0];
    const labelSubtext = (
      <React.Fragment>
        This device has not yet been verified. In order to receive the test message, please{' '}
        <ModalTrigger
          modal={
            <AddedDeviceVerifierModal pendingSmsDeviceId={firstDevice.id} refetch={refetch} />
          }>
          <Link>verify the number</Link>
        </ModalTrigger>
        .
      </React.Fragment>
    );

    return (
      <PhoneAvatarWithText
        data-test={`pending-device-${firstDevice.id}`}
        actionButtonAttributes={actionButtonAttributes}
        actionDisabled
        label={<TextWithSubtext title={firstDevice.label} subtext={labelSubtext} />}
      />
    );
  }

  return (
    <PhoneAvatarWithText
      center
      data-test="add-device"
      actionButtonAttributes={actionButtonAttributes}
      actionDisabled
      label={
        <ModalTrigger
          modal={
            <LinkDeviceModal onRequestHide={() => {}} onDone={refetch} deviceType="phoneNumber" />
          }>
          <Link>Add a number</Link>
        </ModalTrigger>
      }
    />
  );
}

function TextWithSubtext({ title, subtext }: { title: React.ReactNode; subtext: React.ReactNode }) {
  return (
    <Text lineHeight="medium">
      <Text element="div">{title}</Text>
      <Text size="xsmall" color="graphite">
        {subtext}
      </Text>
    </Text>
  );
}

function PhoneAvatarWithText({
  label,
  center,
  actionButtonAttributes,
  actionDisabled,
  deviceUuid,
}: {
  label: React.ReactNode;
  center?: boolean;
  actionButtonAttributes?: UserSMSDeviceActionButtonAttributes;
  actionDisabled?: boolean;
  deviceUuid?: string;
}) {
  const avatarComponent = (
    <MediaObject gutter="small" center={center}>
      <Avatar glyphName="phone-filled" size="large" />
      {label}
    </MediaObject>
  );

  if (actionButtonAttributes) {
    return (
      <MediaObject center gutter="small" figure="right">
        {avatarComponent}
        <Button
          data-test={actionButtonAttributes.dataTestLabel || 'action-button'}
          onClick={() => actionButtonAttributes.onClick(deviceUuid)}
          disabled={!!actionDisabled}>
          {actionButtonAttributes.label}
        </Button>
      </MediaObject>
    );
  }

  return avatarComponent;
}
